// Help (docs/cockpit/spec.md, Setup; ADR-0020): the business's help topics, generated from its own Business Tree, in the
// person's language (English, or the Hindi of #221, a page without all its Hindi in English with a note), only those
// their Roles let them see, and searchable; how to reach support; and "Ask LAMP", which opens chat.
import { useEffect, useState } from "react";
import { ApiError, type Api } from "../api.js";
import { noticeHref } from "../legal/Notice.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import type { HelpView } from "./api.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

export function Help({ api, onAskLamp }: { api: Api; onAskLamp: () => void }) {
  const w = useWords();
  const { language } = useLanguage();
  const [words, setWords] = useState("");
  const [help, setHelp] = useState<HelpView | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  // Each change of words or language asks again; an answer to an older question is dropped.
  useEffect(() => {
    let live = true;
    api.help.topics(language, words).then(
      (view) => {
        if (!live) return;
        setHelp(view);
        setProblem(undefined);
      },
      (error: unknown) => live && setProblem(problemOf(error, w)),
    );
    return () => {
      live = false;
    };
  }, [api, language, words, w]);

  return (
    <>
      <p>
        <button type="button" className="btn btn-go" onClick={onAskLamp}>
          {w.helpAsk}
        </button>
      </p>
      <form role="search" onSubmit={(event) => event.preventDefault()}>
        <label className="field">
          {w.helpSearch}
          <input
            type="search"
            value={words}
            placeholder={w.helpSearchHint}
            onChange={(event) => setWords(event.target.value)}
          />
        </label>
      </form>
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : !help ? (
        <p role="status">{w.helpLoading}</p>
      ) : (
        <>
          <p role="status" className="muted">
            {help.matched === 0 ? w.helpNone : w.helpMatched(help.topics.length, help.matched)}
          </p>
          {help.topics.map((t) => (
            // The packs' own words (#221): Hindi where the packs translate them, else English, which the page marks.
            <article key={t.id} className="card" lang={t.language} data-pack="">
              <h3>{t.title}</h3>
              <p>{t.text}</p>
              {t.fallbackNote ? (
                <p className="small muted" lang={language}>
                  {t.fallbackNote}
                </p>
              ) : null}
            </article>
          ))}
        </>
      )}
      <h2>{w.helpSupport}</h2>
      <p>{w.helpSupportAgent}</p>
      <p>{w.helpSupportPeople}</p>
      <p>
        {w.helpGrievance} <a href={noticeHref("contact-and-grievances", language)}>{w.helpGrievanceLink}</a>
      </p>
    </>
  );
}
